import { TemplateMessage } from './TemplateMessage';
import React from 'react';
import { TextAndIcon } from '../../../commons/components/TextAndIcon';
import { ArrowRightUpIcon, PaperPlaneIcon } from '../../../assets/icons';
import { growegyAIConfig } from '../../../commons/config';
import {
  CHAT_TYPE_BUSINESS_PLAN,
  CHAT_TYPE_CREATE_PROGRAM,
  CHAT_TYPE_GENERAL,
  ChatType,
  PlaceholderMetadataDto,
} from '../../dto';
import { AI_MESSAGE_SENDER, Prompt } from '../../types';
import { breakDownLibraryPrompt } from '../../util';
import { PromptWithPlaceholders } from '../../library/components/PromptWithPlaceholders';

type BasicPrompt = {
  content: string;
  placeholders: PlaceholderMetadataDto[];
};

const SAMPLE_PROMPT_DTOS: BasicPrompt[] = [
  {
    content:
      'Help me generate content ideas for my blog that are related to [product or service] and will attract my target [audience].',
    placeholders: [
      {
        type: 'ListMetadata',
        name: '[product or service]',
        values: [{ type: 'StringMetadata', name: '' }],
      },
      {
        type: 'ListMetadata',
        name: '[audience]',
        values: [{ type: 'StringMetadata', name: '' }],
      },
    ],
  },
  {
    content:
      'Write a post for [social media] to generate interest in [product or service]. Target is [audience].',
    placeholders: [
      {
        type: 'ListMetadata',
        name: '[social media]',
        values: [{ type: 'StringMetadata', name: '' }],
      },
      {
        type: 'ListMetadata',
        name: '[product or service]',
        values: [{ type: 'StringMetadata', name: '' }],
      },
      {
        type: 'ListMetadata',
        name: '[audience]',
        values: [{ type: 'StringMetadata', name: '' }],
      },
    ],
  },
  {
    content: 'Write a cold outbound email to a prospective customer for [product or service].',
    placeholders: [
      {
        type: 'ListMetadata',
        name: '[product or service]',
        values: [{ type: 'StringMetadata', name: '' }],
      },
    ],
  },
  {
    content:
      'I’m writing a blog post for [company] about [topic]. How should I structure the post?',
    placeholders: [
      {
        type: 'StringMetadata',
        name: '[company]',
      },
      {
        type: 'ListMetadata',
        name: '[product or service]',
        values: [{ type: 'StringMetadata', name: '' }],
      },
    ],
  },
];

const SAMPLE_GENERAL_PROMPTS: Prompt[] = SAMPLE_PROMPT_DTOS.map((x) => {
  const parsedSpans = breakDownLibraryPrompt(x.content);
  const parsedContent = parsedSpans.map((s) => s.text).join('');
  return {
    id: '',
    title: parsedContent,
    content: x.content,
    categories: [],
    metadata: { placeholders: [] },
    parsedSpans,
    parsedContent,
  };
});

const SAMPLE_CREATE_PROGRAMS_PROMPTS = [
  'Create 5 social media polls about private money lending targeted to first time home buyers.',
  'Create 5 social media programs promoting the book release and schedule them for every Monday.',
  'Create programs to schedule a Business Strategy Meeting with my Growegy Account Manager on the 21st of every month for the next 6 months.',
];

const SAMPLE_BUSINESS_PLAN_PROMPTS = [
  'Help me write this section.',
  'What are the key aspects to tackle in this section?',
  'What are the best practices for writing this section?',
  'What to avoid when writing this section?',
];

const SamplePrompt = (props: {
  prompt: string | Prompt;
  dataTest: string;
  onClick: (content: string | Prompt) => void;
}) => {
  const { prompt, dataTest, onClick } = props;
  return (
    <button className="intro-message__sample" data-test={dataTest} onClick={() => onClick(prompt)}>
      <TextAndIcon
        text={typeof prompt === 'string' ? prompt : undefined}
        icon={<PaperPlaneIcon className="intro-message__send-icon" />}
        textDataTest="sample-prompt__text"
        iconPosition="right"
        textAlign="left"
        textClassName="intro-message__sample-text"
      >
        {typeof prompt !== 'string' && (
          <div className="text-left" data-test="sample-prompt__text">
            <PromptWithPlaceholders
              libraryPromptContent={prompt.content}
              className="prompt-with-placeholders__text--light900"
            />
          </div>
        )}
      </TextAndIcon>
    </button>
  );
};

export const IntroMessage = (props: {
  chatType: ChatType;
  onSend: (content: string) => void;
  onPromptClick: (prompt: Prompt) => void;
  onOpenLibraryClick?: () => void;
}) => {
  const { chatType, onSend, onPromptClick, onOpenLibraryClick } = props;
  return (
    <TemplateMessage sender={AI_MESSAGE_SENDER} chatType={chatType}>
      <div data-test="intro-message">
        <div className="growegy-font14--weight700">
          {chatType === CHAT_TYPE_CREATE_PROGRAM
            ? 'I am Growegy AI Creator and can create programs for you.'
            : chatType === CHAT_TYPE_BUSINESS_PLAN
            ? 'I am Growegy AI, your partner in crafting a professional business plan tailored to your needs.'
            : 'I am Growegy AI, your creative marketing collaborator.'}
        </div>
        <div>
          Not sure where to start? You can try these prompts or write your own in the field below.
        </div>
        <div className="intro-message__sample-list-container">
          <>
            {chatType === CHAT_TYPE_GENERAL
              ? SAMPLE_GENERAL_PROMPTS.map((x, i) => (
                  <SamplePrompt
                    key={`sample_prompt_${i}`}
                    prompt={x}
                    dataTest={`intro-message__sample-prompt-${i}`}
                    onClick={() => typeof x === 'object' && onPromptClick(x)}
                  />
                ))
              : (chatType === CHAT_TYPE_CREATE_PROGRAM
                  ? SAMPLE_CREATE_PROGRAMS_PROMPTS
                  : SAMPLE_BUSINESS_PLAN_PROMPTS
                ).map((x, i) => (
                  <SamplePrompt
                    key={`sample_prompt_${i}`}
                    prompt={x}
                    dataTest={`intro-message__sample-prompt-${i}`}
                    onClick={() => onSend(x)}
                  />
                ))}
            {growegyAIConfig.libraryEnabled &&
              chatType === CHAT_TYPE_GENERAL &&
              onOpenLibraryClick && (
                <button
                  className="intro-message__sample"
                  data-test="intro-message__open-library"
                  onClick={onOpenLibraryClick}
                >
                  <TextAndIcon
                    text="View more in Library"
                    icon={<ArrowRightUpIcon className="intro-message__send-icon" />}
                    iconPosition="right"
                    textAlign="left"
                    textClassName="intro-message__open-library"
                  />
                </button>
              )}
          </>
        </div>
      </div>
    </TemplateMessage>
  );
};
